import { useMemo } from 'react';

import {
  ASSESSMENT_DETAILS_CSV_TYPE_ID,
  EMAIL_TYPE_ID,
  MAX_PAGE_SIZE,
  SERVICE_NOW_TYPE_ID,
  WEBHOOK_TYPE_ID
} from '@attackiq/constants';

import { useIntegrationTypes, type NotificationIntegrationType } from '../integration_types/useIntegrationTypes';
import { useIntegrations, type Integration } from '../integrations/useIntegrations';

import { type NotificationMetaData, type ProjectIntegration, useProjectIntegrations } from './useProjectIntegrations';

export interface NotificationProviderViewModel {
  id?: string;
  type_id: string;
  integration_id?: string;
  name: string;
  description: string;
  enabled: boolean;
  integrationEnabled: boolean;
  configured: boolean;
  threshold: number;
  recipients?: string[];
  meta_data: NotificationMetaData;
  global_meta_data?: NotificationMetaData;
}

const DEFAULT_PASS_PERCENTAGE = 65;

const DESCRIPTIONS_BY_TYPE_ID: Record<string, string> = {
  [EMAIL_TYPE_ID]:
    'For scheduled Assessments only, send an email to the specified email addresses when the Assessment completes and the pass rate is below the set threshold.',
  [WEBHOOK_TYPE_ID]:
    'Calls the endpoint configured at the assessment level with the provided user/password, upon assessment completion.',
  [ASSESSMENT_DETAILS_CSV_TYPE_ID]:
    'Before an assessment run begins, send an email to the specified email addresses with a CSV attachment identifying all participating assets and the scenarios scheduled to run on them.',
  [SERVICE_NOW_TYPE_ID]:
    'Provide instance, user and password to enable the AttackIQ Platform to create incidents for an assessment that completes with a pass rate below the set threshold.'
};

const DESCRIPTIONS_BY_NAME: Record<string, string> = {
  Slack:
    'Send a Slack notification to the specified channel (configured in the main Notification section) when the Assessment completes and the pass rate is below the set threshold.',
  Jira: 'Send a JIRA notification to the specified board (configured in the global Notification section) when the Assessment completes and the pass rate is below the set threshold.'
};

const TYPES_WITH_RECIPIENTS = new Set([EMAIL_TYPE_ID, ASSESSMENT_DETAILS_CSV_TYPE_ID]);

const resolveDescription = (typeId: string, name: string, fallback: string): string =>
  DESCRIPTIONS_BY_TYPE_ID[typeId] ?? DESCRIPTIONS_BY_NAME[name] ?? fallback;

const resolveThreshold = (projectIntegration: ProjectIntegration | undefined): number => {
  const stored = projectIntegration?.meta_data?.notification_threshold;
  return stored && stored > 0 ? Math.floor(stored * 100) : DEFAULT_PASS_PERCENTAGE;
};

const buildProvider = (
  notificationType: NotificationIntegrationType,
  integration: Integration | undefined,
  projectIntegration: ProjectIntegration | undefined
): NotificationProviderViewModel => {
  const base: NotificationProviderViewModel = {
    type_id: notificationType.id,
    name: notificationType.name,
    description: resolveDescription(notificationType.id, notificationType.name, notificationType.description),
    enabled: false,
    integrationEnabled: integration?.enabled ?? false,
    configured: false,
    threshold: DEFAULT_PASS_PERCENTAGE,
    meta_data: {}
  };

  if (!integration) return base;

  const integrationMetaData = (integration.meta_data ?? {}) as NotificationMetaData;
  const projectMetaData = projectIntegration?.meta_data ?? {};
  const provider: NotificationProviderViewModel = {
    ...base,
    integration_id: integration.id,
    meta_data: integrationMetaData,
    configured: Object.keys(integrationMetaData).length > 0,
    id: projectIntegration?.id,
    enabled: Boolean(integration.enabled && projectIntegration?.enabled),
    threshold: resolveThreshold(projectIntegration)
  };

  if (TYPES_WITH_RECIPIENTS.has(notificationType.id)) {
    provider.recipients = projectMetaData.recipient_list ?? [];
  }
  if (notificationType.id === WEBHOOK_TYPE_ID) {
    provider.meta_data = projectMetaData;
    provider.configured = Object.keys(projectMetaData).length > 0;
  }
  if (notificationType.id === SERVICE_NOW_TYPE_ID) {
    provider.global_meta_data = integrationMetaData;
    provider.meta_data = projectMetaData;
  }

  return provider;
};

export const useAssessmentNotificationProviders = (projectId: string) => {
  const { data: integrationTypesData, isLoading: isLoadingTypes } = useIntegrationTypes();
  const { data: integrationsData, isLoading: isLoadingIntegrations } = useIntegrations({ ordering: 'created' });
  const { data: projectIntegrationsData, isLoading: isLoadingProjectIntegrations } = useProjectIntegrations({
    project_id: projectId,
    page_size: MAX_PAGE_SIZE
  });

  const providers = useMemo(() => {
    if (!integrationTypesData?.results) return [];

    const integrationByTypeId = new Map<string, Integration>();
    for (const integration of integrationsData?.results ?? []) {
      integrationByTypeId.set(integration.type.id, integration);
    }

    const projectIntegrationByIntegrationId = new Map<string, ProjectIntegration>();
    for (const pi of projectIntegrationsData?.results ?? []) {
      projectIntegrationByIntegrationId.set(pi.integration.id, pi);
    }

    return integrationTypesData.results.map(type => {
      const integration = integrationByTypeId.get(type.id);
      const projectIntegration = integration ? projectIntegrationByIntegrationId.get(integration.id) : undefined;
      return buildProvider(type, integration, projectIntegration);
    });
  }, [integrationTypesData, integrationsData, projectIntegrationsData]);

  return {
    providers,
    isLoading: isLoadingTypes || isLoadingIntegrations || isLoadingProjectIntegrations
  };
};
